import { ClientTable } from '@civilgrid/ui/components';
import { useTableSettings, useTableStoreState } from '@civilgrid/ui/hooks';
import { SkeletonSpinnerCentered } from '@civilgrid/ui/SkeletonLoader';
import { useParams } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import authorizations, { Action } from '~/authorizations';
import { useOrgResolvedFlag } from '~/hooks/useOrgResolvedFlag';
import rootStore from '~/store';
import type { ProjectDetailsParams } from '../..';
import projectDetailsStore from '../../store';
import { UTILITIES_PAGE_REDESIGN_FLAG, type UtilityAgency } from '../common';
import store from '../store';
import {
  compactColumns,
  DownloadAllFilesModal,
  expandedColumns,
  hasAgencyFiles,
  withAssetTypeOptions,
} from './columns';
import { buildAgencyRows, getUtilityRowId, withFileFlags } from './grouping';
import LegacyTable from './legacy';

const AGENCY_NAME_SORT: SortingState = [{ id: 'agencyName', desc: false }];
const getSubRows = (row: UtilityAgency) => row.subRows;
// Cells return plain text, which the ui grid leaves at the page size.
const CELL_TEXT_CLASS = 'text-xs font-noto text-grey-90';

// Search, filters and sort aren't saved, so each project opens fresh, sorted by agency name. The page size comes from
// the table's saved settings.
function useUtilitiesTableState(id: string) {
  const { initialPageSize } = useTableSettings({ id });
  return useTableStoreState({
    initialSorting: AGENCY_NAME_SORT,
    initialPagination: initialPageSize ? { pageIndex: 0, pageSize: initialPageSize } : undefined,
  });
}

export default function TableComponent() {
  const { resolved, enabled } = useOrgResolvedFlag(UTILITIES_PAGE_REDESIGN_FLAG);
  // Wait for this org's flags, so the old table never flashes before the new one.
  if (!resolved) return <SkeletonSpinnerCentered />;
  return enabled ? <UtilitiesClientTable /> : <LegacyTable />;
}

function UtilitiesClientTable() {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { agencies, dataRequests, isFullUtilitiesExperienceEnabled, isLoading, isError, project } = useSnapshot(
    store,
  ) as typeof store;
  const { isExpanded } = useSnapshot(projectDetailsStore);
  const { me, myRole } = useSnapshot(rootStore) as typeof rootStore;

  const data = useMemo(() => {
    const requests = Object.values(dataRequests);
    return withFileFlags(buildAgencyRows(Object.values(agencies), requests), requests);
  }, [agencies, dataRequests]);

  const compactState = useUtilitiesTableState('utilities-compact');
  const expandedState = useUtilitiesTableState('utilities-expanded');

  // Files is left out rather than hidden so it doesn't show up in the columns menu either.
  const visibleExpandedColumns = useMemo(
    () =>
      withAssetTypeOptions(
        isFullUtilitiesExperienceEnabled ? expandedColumns : expandedColumns.filter((c) => c.id !== 'attachments'),
        data,
      ),
    [isFullUtilitiesExperienceEnabled, data],
  );
  const visibleCompactColumns = useMemo(() => withAssetTypeOptions(compactColumns, data), [data]);

  // "Email all utility files" lives in the table's settings menu, alongside the Files column it downloads.
  const [downloadAllOpen, setDownloadAllOpen] = useState(false);
  // Hidden for roles without DownloadData, which the endpoint requires; the table menu has no disabled state.
  const canDownloadData = !!me && !!myRole && authorizations.can(Action.DownloadData);
  // The store keeps the previous project's requests until this project's load.
  const utilitiesLoaded = !isLoading && !isError && project?.id === projectId;
  const showDownloadAll =
    isFullUtilitiesExperienceEnabled && canDownloadData && utilitiesLoaded && hasAgencyFiles(agencies, dataRequests);
  const menuActions = useMemo(
    () =>
      showDownloadAll
        ? [
            {
              label: 'Email all utility files',
              icon: 'download' as const,
              onClick: () => setDownloadAllOpen(true),
              // Kept from the old header button so PostHog autocapture history carries on.
              testId: 'civilsearch-attachments-download-all',
            },
          ]
        : undefined,
    [showDownloadAll],
  );

  if (isExpanded) {
    return (
      <>
        <ClientTable
          key="utilities-expanded"
          id="utilities-expanded"
          aria-labelledby="utilities-top-nav-title"
          data-testid="utilities-table"
          columns={visibleExpandedColumns}
          data={data}
          stateControl={expandedState}
          enableExpanding
          getSubRows={getSubRows}
          getRowId={getUtilityRowId}
          className={CELL_TEXT_CLASS}
          defaultDensity="comfy"
          menuActions={menuActions}
        />
        <DownloadAllFilesModal open={downloadAllOpen} onOpenChange={setDownloadAllOpen} />
      </>
    );
  }

  return (
    <ClientTable
      key="utilities-compact"
      id="utilities-compact"
      aria-labelledby="utilities-top-nav-title"
      data-testid="utilities-table"
      columns={visibleCompactColumns}
      data={data}
      stateControl={compactState}
      enableExpanding
      getSubRows={getSubRows}
      getRowId={getUtilityRowId}
      className={CELL_TEXT_CLASS}
      onRowClick={() => {
        projectDetailsStore.toggleIsExpanded(true);
      }}
    />
  );
}
